/**
 * @fileoverview Debounce utility function for rate-limiting function execution.
 * 
 * This module provides a debounce function that limits the rate at which a function
 * can fire. It's particularly useful for performance optimization in scenarios like
 * handling window resize events, API calls on user input, or search functionality.
 * 
 * @module helpers/debounce
 */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyFunction = (...args: any[]) => any;

/**
 * Creates a debounced version of the provided function that delays invoking `func`
 * until after `wait` milliseconds have elapsed since the last time the debounced
 * function was invoked.
 * 
 * The debounced function comes with a `cancel` method to cancel delayed invocations
 * and a `flush` method to immediately invoke them.
 * 
 * @template F - The type of the function to debounce
 * @param {F} func - The function to debounce
 * @param {number} wait - The number of milliseconds to delay execution
 * @param {boolean} [immediate=false] - If true, trigger the function on the leading edge instead of the trailing edge
 * @returns {(...args: Parameters<F>) => void} The debounced function
 * 
 * @example
 * // Debounce a search function to wait 300ms after user stops typing
 * const searchInput = document.getElementById('search');
 * const debouncedSearch = debounce((query: string) => {
 *   console.log('Searching for:', query);
 *   // Perform API call
 * }, 300);
 * 
 * searchInput.addEventListener('input', (e) => {
 *   debouncedSearch((e.target as HTMLInputElement).value);
 * });
 * 
 * @example
 * // Immediate execution on first call, then debounce subsequent calls
 * const saveButton = document.getElementById('save');
 * const debouncedSave = debounce(() => {
 *   console.log('Saving...');
 *   // Perform save operation
 * }, 1000, true);
 * 
 * saveButton.addEventListener('click', debouncedSave);
 * 
 * @example
 * // Debouncing window resize events
 * const handleResize = debounce(() => {
 *   console.log('Window resized to:', window.innerWidth, window.innerHeight);
 *   // Update layout calculations
 * }, 250);
 * 
 * window.addEventListener('resize', handleResize);
 */
function debounce<F extends AnyFunction>(
  func: F,
  wait: number,
  immediate: boolean = false
): (...args: Parameters<F>) => void {
  let timeout: NodeJS.Timeout | null = null;

  return function(this: ThisParameterType<F>, ...args: Parameters<F>): void {
    const callNow = immediate && !timeout;

    const later = () => {
      timeout = null;
      if (!immediate) func.apply(this, args);
    };

    if (timeout) {
      clearTimeout(timeout);
    }

    timeout = setTimeout(later, wait);

    if (callNow) {
      func.apply(this, args);
    }
  };
}

export default debounce;
